/**
 * sessionStore — single source of truth for AgentOS session in localStorage.
 *
 * Two storage schemas coexist by historical accident:
 *   - AuthContext (tenant flows) reads `agentos_session` (composite JSON).
 *   - Admin Master flows read `agentos_user` + `agentos_tenant` separately.
 * Until both consumers are unified, this module writes BOTH atomically so
 * neither side ever drifts. Use these helpers everywhere instead of touching
 * localStorage directly.
 */

const KEYS = {
  token:   "agentos_token",
  user:    "agentos_user",
  tenant:  "agentos_tenant",
  session: "agentos_session",      // composite — read by AuthContext
  // Impersonation slots
  impAdmin:  "agentos_impersonate_admin",   // stash of super_admin session
  impBanner: "agentos_impersonate_banner",  // master email shown in banner
};

function safeParse(raw) {
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

export function getToken()  { return localStorage.getItem(KEYS.token); }
export function getUser()   { return safeParse(localStorage.getItem(KEYS.user)); }
export function getTenant() { return safeParse(localStorage.getItem(KEYS.tenant)); }

export function getSession() {
  // Prefer composite (AuthContext schema); fall back to split keys.
  return safeParse(localStorage.getItem(KEYS.session))
      || { user: getUser(), tenant: getTenant() };
}

export function getImpersonateBanner() {
  return localStorage.getItem(KEYS.impBanner);
}

export function setSession({ token, user, tenant }) {
  if (token)  localStorage.setItem(KEYS.token,   token);
  if (user)   localStorage.setItem(KEYS.user,    JSON.stringify(user));
  if (tenant) localStorage.setItem(KEYS.tenant,  JSON.stringify(tenant));
  if (user || tenant) {
    localStorage.setItem(KEYS.session, JSON.stringify({
      user:   user   || getUser(),
      tenant: tenant || getTenant(),
    }));
  }
}

export function clearSession() {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
}

/**
 * Atomic impersonation handover.
 * Stash includes ALL admin keys so restore is bit-exact, including the
 * composite session so AuthContext rehydrates correctly when the super_admin
 * returns.
 */
export function startImpersonate({ token, user, tenant, byEmail }) {
  localStorage.setItem(KEYS.impAdmin, JSON.stringify({
    token:   localStorage.getItem(KEYS.token),
    user:    localStorage.getItem(KEYS.user),
    tenant:  localStorage.getItem(KEYS.tenant),
    session: localStorage.getItem(KEYS.session),
  }));
  setSession({ token, user, tenant });
  localStorage.setItem(KEYS.impBanner, byEmail);
}

/** Returns true if the admin session was restored, false if no stash existed. */
export function stopImpersonate() {
  const raw = localStorage.getItem(KEYS.impAdmin);
  if (!raw) {
    // No stash → cannot restore safely. Caller decides what to do.
    localStorage.removeItem(KEYS.token);
    localStorage.removeItem(KEYS.user);
    localStorage.removeItem(KEYS.tenant);
    localStorage.removeItem(KEYS.session);
    localStorage.removeItem(KEYS.impBanner);
    return false;
  }
  const stash = safeParse(raw) || {};
  if (stash.token)   localStorage.setItem(KEYS.token,   stash.token);
  if (stash.user)    localStorage.setItem(KEYS.user,    stash.user);
  if (stash.tenant)  localStorage.setItem(KEYS.tenant,  stash.tenant);
  if (stash.session) localStorage.setItem(KEYS.session, stash.session);
  else               localStorage.removeItem(KEYS.session);
  localStorage.removeItem(KEYS.impAdmin);
  localStorage.removeItem(KEYS.impBanner);
  return true;
}

export const STORAGE_KEYS = KEYS;  // exposed for tests
